import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import { toast } from 'react-toastify'
import { useAuthentication } from '../../hooks/useAuthentication'
import { ADD_VOUCHER_TAG, GET_TAGS, REMOVE_VOUCHER_TAG } from '../../api/tag'
import { Tag } from '../../types'
import { tagListPath } from './tagLinks'
import ManageTagsDialog from './ManageTagsDialog'
import { tagChipSx } from './tagChipStyle'

// Mirrors the backend cap in vhs-gql/src/lib/tags.ts.
const TAG_NAME_MAX_LENGTH = 40

type Option = Tag | { id: null; name: string; create: true }

const filter = createFilterOptions<Option>()

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '0.8rem',
  mb: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: 'text.secondary',
} as const

type Props = {
  voucherId: string
  initialTags?: Tag[]
}

// Tags section of the voucher details page. Anyone who can see the voucher
// can add or remove tags, in any state (voucher-tags.md §4).
const VoucherTags = ({ voucherId, initialTags }: Props) => {
  const history = useHistory()
  const { authState } = useAuthentication()
  const [tags, setTags] = useState<Tag[]>(initialTags ?? [])
  const [input, setInput] = useState('')
  const [manageOpen, setManageOpen] = useState(false)

  useEffect(() => {
    setTags(initialTags ?? [])
  }, [voucherId, initialTags])

  const allTags = useQuery(GET_TAGS, { fetchPolicy: 'cache-and-network' })
  const [addVoucherTag, { loading: adding }] = useMutation(ADD_VOUCHER_TAG)
  const [removeVoucherTag] = useMutation(REMOVE_VOUCHER_TAG)

  const options: Option[] = (allTags.data?.tags ?? []).filter(
    (t: Tag) => !tags.some((own) => own.id === t.id),
  )

  const add = async (variables: { tagId?: number; name?: string }) => {
    try {
      const { data } = await addVoucherTag({
        variables: { voucherId, ...variables },
      })
      setTags(data.addVoucherTag)
      setInput('')
      allTags.refetch()
    } catch (err: any) {
      toast.error(err.message || 'Failed to add tag')
    }
  }

  const remove = async (tag: Tag) => {
    try {
      const { data } = await removeVoucherTag({
        variables: { voucherId, tagId: tag.id },
      })
      setTags(data.removeVoucherTag)
      allTags.refetch()
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove tag')
    }
  }

  const handleSelect = (value: Option | string | null) => {
    if (!value) return
    if (typeof value === 'string') {
      const name = value.trim()
      if (name) add({ name })
    } else if (value.id === null) {
      add({ name: value.name })
    } else {
      add({ tagId: value.id })
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" sx={sectionTitleSx}>
        <LocalOfferOutlinedIcon fontSize="small" /> Tags
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          onClick={() => setManageOpen(true)}
          sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
        >
          Manage tags
        </Button>
      </Typography>

      <Autocomplete<Option, false, false, true>
        freeSolo
        size="small"
        value={null}
        inputValue={input}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') setInput(value.slice(0, TAG_NAME_MAX_LENGTH))
        }}
        onChange={(_, value) => handleSelect(value)}
        options={options}
        disabled={adding}
        selectOnFocus
        handleHomeEndKeys
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
        filterOptions={(opts, params) => {
          const filtered = filter(opts, params)
          const typed = params.inputValue.trim()
          const exists =
            !!typed &&
            [...opts, ...tags].some(
              (o) => o.name.toLowerCase() === typed.toLowerCase(),
            )
          if (typed && !exists) {
            filtered.push({ id: null, name: typed, create: true })
          }
          return filtered
        }}
        renderOption={(props, o) => {
          const { key, ...rest } = props as any
          return (
            <li key={key} {...rest}>
              {o.id === null ? (
                <em>Create tag “{o.name}”</em>
              ) : (
                <>
                  {o.name}
                  {o.voucher_count !== undefined && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({o.voucher_count})
                    </Typography>
                  )}
                </>
              )}
            </li>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add a tag"
            placeholder="Type to search or create"
            sx={{ bgcolor: 'white' }}
          />
        )}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {tags.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            No tags yet.
          </Typography>
        ) : (
          tags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              clickable
              onClick={() =>
                history.push(tagListPath(tag.id, authState.authUser))
              }
              onDelete={() => remove(tag)}
              sx={tagChipSx}
            />
          ))
        )}
      </Box>

      <ManageTagsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={(change) => {
          allTags.refetch()
          setTags((prev) =>
            change.deletedId !== undefined
              ? prev.filter((t) => t.id !== change.deletedId)
              : prev.map((t) =>
                  t.id === change.renamed?.id ? change.renamed : t,
                ),
          )
        }}
      />
    </Paper>
  )
}

export default VoucherTags
